import {
  ComposedChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { F, FF, P } from '../utils/format';
import { Card } from '../components/Card';
import { Stat } from '../components/Stat';
import { Heading } from '../components/Heading';
import { Slider } from '../components/Slider';
import { Toggle } from '../components/Toggle';
import { Table } from '../components/Table';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { Briefcase, DollarSign, Zap, TrendingUp } from '../components/icons';
import { useApp } from '../hooks/useApp';
import InsightBanner from '../features/motivation/InsightBanner';
import { careerInsights } from '../features/motivation/motivationEngine';

export default function Career() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const w = proj.find(d => d.a >= s.startAge) || p0;
  const insights = careerInsights(s, proj, pF);
  return (
    <Stack>
      <Card c={c} sx={{ padding: "28px 24px" }}>
        <Stat l={`Projected Salary at ${pF.a}`} v={F(pF.base)} sub={`${F(pF.comp)} total comp · ${pF.taxRate}% effective / ${pF.margRate}% marginal (ON)`} big c={c} />
      </Card>

      <InsightBanner insights={insights} />

      <Grid>
        <Card c={c} delay={0.05}>
          <Heading ic={Briefcase} c={c}>Compensation</Heading>
          <Slider l="Base Salary" v={s.salary} fn={(v) => up("salary", v)} min={60000} max={200000} step={5000} f="$" c={c} />
          <Slider l="Bonus Rate" v={s.bonusR} fn={(v) => up("bonusR", v)} min={0} max={0.3} step={0.01} f="%" c={c} />
          <Slider l="Annual Raise" v={s.salGr} fn={(v) => up("salGr", v)} min={0.02} max={0.12} step={0.005} f="%" c={c} />
          <Slider l="Career Start Age" v={s.startAge} fn={(v) => up("startAge", v)} min={22} max={28} c={c} />
        </Card>
        <Card c={c} delay={0.1}>
          <Heading ic={Zap} c={c}>Certifications</Heading>
          <Toggle l="PMP — Project Management" on={s.cPMP} fn={(v) => up("cPMP", v)} c={c} />
          <Toggle l="AWS Solutions Architect" on={s.cAWS} fn={(v) => up("cAWS", v)} c={c} />
          <Toggle l="CISSP — Cybersecurity" on={s.cCISSP} fn={(v) => up("cCISSP", v)} c={c} />
          <div style={{
            marginTop: 16, padding: "12px 16px", borderRadius: 12,
            background: c.card2, fontSize: 13, display: "flex", justifyContent: "space-between",
          }}>
            <span style={{ color: c.muted }}>Active boost</span>
            <span style={{ color: c.blue, fontWeight: 700 }}>
              +{P((s.cPMP ? 0.05 : 0) + (s.cAWS ? 0.05 : 0) + (s.cCISSP ? 0.05 : 0))}
            </span>
          </div>
        </Card>
      </Grid>

      <Card c={c} delay={0.12}>
        <Heading ic={DollarSign} c={c}>Tax Breakdown (Age {w.a})</Heading>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          {[
            { l: "Federal", v: w.taxFed, cl: c.blue },
            { l: "Provincial", v: w.taxProv, cl: c.purple },
            { l: "CPP/CPP2", v: w.taxCPP, cl: c.orange },
            { l: "EI", v: w.taxEI, cl: c.teal },
          ].map(x => (
            <div key={x.l} style={{ flex: "1 1 120px", padding: "12px 16px", borderRadius: 12, background: c.card2 }}>
              <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{x.l}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: x.cl, marginTop: 4 }}>{FF(x.v)}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ padding: "10px 16px", borderRadius: 12, background: c.card2, fontSize: 13, display: "flex", gap: 8 }}>
            <span style={{ color: c.muted }}>Effective</span>
            <span style={{ color: c.fg, fontWeight: 700 }}>{w.taxRate}%</span>
          </div>
          <div style={{ padding: "10px 16px", borderRadius: 12, background: c.card2, fontSize: 13, display: "flex", gap: 8 }}>
            <span style={{ color: c.muted }}>Marginal</span>
            <span style={{ color: c.red, fontWeight: 700 }}>{w.margRate}%</span>
          </div>
          {w.estRefund > 0 && (
            <div style={{ padding: "10px 16px", borderRadius: 12, background: s.dark ? "rgba(48,209,88,0.08)" : "rgba(48,209,88,0.05)", fontSize: 13, display: "flex", gap: 8 }}>
              <span style={{ color: c.muted }}>Est. RRSP Refund</span>
              <span style={{ color: c.green, fontWeight: 700 }}>{FF(w.estRefund)}</span>
            </div>
          )}
        </div>
      </Card>

      <Card c={c} delay={0.15}>
        <Heading ic={TrendingUp} c={c}>Compensation Growth</Heading>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={proj.filter((d) => d.a >= s.startAge)}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
            <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => FF(v)} {...tt} />
            <Bar dataKey="bon" fill={c.orange} name="Bonus" radius={[4, 4, 0, 0]} />
            <Bar dataKey="base" fill={c.blue} name="Base" radius={[4, 4, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      <Card c={c} delay={0.2}>
        <Table h={["Age", "Base", "Bonus", "Total", "Eff %", "Marg %", "After Tax"]}
          r={proj.filter((d) => d.a >= s.startAge).map((d) => [d.a, FF(d.base), FF(d.bon), FF(d.comp), `${d.taxRate}%`, `${d.margRate}%`, FF(d.afterTax)])} c={c} />
      </Card>
    </Stack>
  );
}
